import React, { useState, useEffect } from 'react'
import { alsokTheme, componentThemes, themeHelpers } from '../styles/alsok-theme'
import { DEFAULT_RECEPTION_STATUS, loadReceptionStatus, saveReceptionStatus } from '../utils/receptionStatusDefaults'
import { useReceptionStatus } from '../hooks/useReceptionStatus'
import { usePlanFeatures } from '../hooks/usePlanFeatures'

const AdminDashboard = ({ onClose, onModeTransition, onReceptionStatusChange, receptionStatus: initialReceptionStatus }) => {
  const [activeTab, setActiveTab] = useState('overview')
  const [visitorLogs, setVisitorLogs] = useState([])
  const [stats, setStats] = useState(null)
  const [systemStatus, setSystemStatus] = useState(null)
  const [envStatus, setEnvStatus] = useState(null)
  
  // グローバル状態管理を使用
  const receptionStatusHook = useReceptionStatus()
  const receptionStatus = receptionStatusHook.status
  
  // プラン機能制御
  const { currentPlan, planFeatures, features } = usePlanFeatures()
  
  // デモ版用のサンプルデータ
  const [demoData, setDemoData] = useState({
    // 概要タブデータ
    overview: {
      systemStatus: {
        uptime: '0日 19時間 20分',
        memoryUsage: '21.7 MB / 23.3 MB',
        databaseStatus: '正常',
        visitorCount: 0,
        faceAuthCount: 0,
        meetingRoomUsage: '0部屋'
      },
      todayActivity: {
        '00時台': 1,
        '01時台': 5,
        '21時台': 1,
        '23時台': 2
      },
      meetingRoomStats: {
        '応接室': 3,
        '会議室C': 2,
        '会議室B': 2,
        '会議室A': 2
      },
      hourlyVisitors: {
        '00時台': 1,
        '01時台': 5,
        '21時台': 1,
        '23時台': 2
      }
    },
    // 来訪者管理タブデータ
    visitorManagement: {
      currentVisitors: [
        {
          id: 1,
          name: '田中太郎',
          company: 'ABC商事株式会社',
          contactPerson: '山田花子',
          department: '営業部',
          email: 'tanaka@abc-trading.co.jp',
          phone: '03-1234-5678',
          time: '10:00',
          duration: '60分',
          meetingRoom: '会議室A',
          purpose: '新サービスの提案・打ち合わせ',
          attendees: '1名',
          status: '予定',
          faceAuth: true
        },
        {
          id: 2,
          name: '佐藤美香',
          company: 'XYZ技術研究所',
          contactPerson: '鈴木一郎',
          department: '技術部',
          email: 'sato@xyz-tech.co.jp',
          phone: '03-9876-5432',
          time: '14:00',
          duration: '90分',
          meetingRoom: '大会議室',
          purpose: 'セキュリティシステム導入検討',
          attendees: '2名',
          status: '確認済み',
          faceAuth: false
        },
        {
          id: 3,
          name: '林由美子',
          company: '株式会社PQR商事',
          contactPerson: '加藤雄介',
          department: '総務部',
          email: 'hayashi@pqr-trading.co.jp',
          phone: '03-8888-9999',
          time: '15:15',
          duration: '60分',
          meetingRoom: '応接室',
          purpose: '来客管理システム運用相談',
          attendees: '1名',
          status: '確認済み',
          faceAuth: true
        }
      ]
    },
    // ログ管理タブデータ
    logManagement: [
      {
        id: 1,
        timestamp: '2025/07/16 10:36:21',
        visitorName: '田中太郎',
        company: 'ABC商事株式会社',
        meetingRoom: '会議室A',
        faceAuth: true,
        notification: true,
        checkIn: '2025/07/16 10:36:21',
        checkOut: '-',
        status: 'チェックイン済み'
      },
      {
        id: 2,
        timestamp: '2025/07/16 8:36:21',
        visitorName: '山田花子',
        company: 'XYZ技研',
        meetingRoom: '会議室B',
        faceAuth: false,
        notification: true,
        checkIn: '2025/07/16 8:36:21',
        checkOut: '2025/07/16 10:15:30',
        status: 'チェックアウト済み'
      },
      {
        id: 3,
        timestamp: '2025/07/16 6:36:21',
        visitorName: 'ジョン・スミス',
        company: 'Global Solutions Inc.',
        meetingRoom: '応接室',
        faceAuth: true,
        notification: true,
        checkIn: '2025/07/16 6:36:21',
        checkOut: '2025/07/16 8:20:15',
        status: 'チェックアウト済み'
      },
      {
        id: 4,
        timestamp: '2025/07/15 10:36:21',
        visitorName: '中村一郎',
        company: 'セキュリティサービス',
        meetingRoom: '会議室A',
        faceAuth: true,
        notification: true,
        checkIn: '2025/07/15 10:36:21',
        checkOut: '2025/07/15 12:10:45',
        status: 'チェックアウト済み'
      },
      {
        id: 5,
        timestamp: '2025/07/15 9:36:21',
        visitorName: '佐々木美咲',
        company: 'デザイン工房',
        meetingRoom: '会議室C',
        faceAuth: false,
        notification: true,
        checkIn: '2025/07/15 9:36:21',
        checkOut: '2025/07/15 11:45:20',
        status: 'チェックアウト済み'
      },
      {
        id: 6,
        timestamp: '2025/07/13 10:36:21',
        visitorName: '木村達也',
        company: 'マーケティング研究所',
        meetingRoom: '会議室C',
        faceAuth: false,
        notification: true,
        checkIn: '2025/07/13 10:36:21',
        checkOut: '2025/07/13 12:25:10',
        status: 'チェックアウト済み'
      }
    ],
    // 利用者分析タブデータ
    userAnalysis: {
      organizerRanking: [
        { name: '中村太郎', department: '営業部', bookings: 1, avgDuration: '2.5時間', usage: 2, approvalRate: 2 },
        { name: '佐藤美香', department: '技術部', bookings: 1, avgDuration: '1.3時間', usage: 1, approvalRate: 1 },
        { name: '田中明美', department: '総務部', bookings: 1, avgDuration: '1.8時間', usage: 1, approvalRate: 1 },
        { name: '鈴木雄一', department: '企画部', bookings: 1, avgDuration: '2.2時間', usage: 1, approvalRate: 1 },
        { name: '営業マーケティング', department: '営業部', bookings: 1, avgDuration: '1.5時間', usage: 1, approvalRate: 1 }
      ],
      departmentStats: {
        '開発部': { color: '#4169E1', percentage: 25 },
        '営業部': { color: '#32CD32', percentage: 30 },
        '総務部': { color: '#FF8C00', percentage: 20 },
        '人事部': { color: '#A0522D', percentage: 15 },
        'マーケティング部': { color: '#DA70D6', percentage: 10 }
      },
      userTypes: {
        '初回ユーザー': { count: 5, percentage: 100.0 }
      },
      departmentDetails: [
        { department: '開発部', bookings: 1, avgDuration: '2.0時間', percentage: 20.0 },
        { department: '営業部', bookings: 1, avgDuration: '2.0時間', percentage: 20.0 }
      ]
    },
    // エラーログデータ
    errorLogs: [
      {
        id: 1,
        level: 'INFO',
        timestamp: '2025/07/17 10:22:39',
        source: 'ConfigManager',
        message: 'システム設定更新',
        details: 'Configuration reloaded successfully',
        category: 'info'
      },
      {
        id: 2,
        level: 'ERROR',
        timestamp: '2025/07/17 10:22:39',
        source: 'CameraService',
        message: 'WebRTCストリーム接続失敗',
        details: 'Camera device not found or permission denied',
        category: 'error'
      },
      {
        id: 3,
        level: 'WARNING',
        timestamp: '2025/07/17 10:22:39',
        source: 'SpeechService',
        message: 'Azure Speech API使用量警告',
        details: 'Monthly quota 80% consumed',
        category: 'warning'
      },
      {
        id: 4,
        level: 'INFO',
        timestamp: '2025/07/17 10:22:39',
        source: 'BackupService',
        message: 'データベースバックアップ完了',
        details: 'Daily backup completed successfully',
        category: 'info'
      }
    ],
    // メール監視データ
    mailMonitoring: [
      {
        id: 1,
        status: '送信',
        timestamp: '2025/07/17 10:00',
        subject: '来訪者登録完了のお知らせ - 田中太郎様',
        from: 'reception@alsok-demo.co.jp',
        to: 'yamada.hanako@abc-trading.co.jp',
        content: '田中太郎様の来訪者登録が完了しました。予定時刻：10:00、会議室A...',
        tags: ['通知', '来訪者通知']
      },
      {
        id: 2,
        status: '送信',
        timestamp: '2025/07/17 08:30',
        subject: '面会予約確認 - セキュリティシステム導入検討',
        from: 'security@alsok-demo.co.jp',
        to: 'suzuki.ichiro@xyz-tech.co.jp',
        content: '佐藤美香様との面会予約を確認いたします。日時：本日14:00、場所：大会議室...',
        tags: ['通知', '来訪者通知']
      }
    ],
    // 設定データ
    settings: {
      apiStatus: {
        'Azure Face API': '設定済み',
        'Azure Speech API': '設定済み',
        'Microsoft Graph API': '設定済み',
        'メール設定': '未設定',
        'Teams Webhook': '未設定'
      },
      notes: [
        'Azure API の使用量を定期的に確認してください',
        '来訪ログは定期的にバックアップ・削除してください',
        '顔認証データは会議終了後24時間で自動削除されます',
        'Teams通知が機能しない場合は、Graph API の権限設定を確認してください',
        'メール送信機能を利用する場合は、SMTP設定を正しく行ってください'
      ]
    },
    // 来客登録一覧データ
    visitorRegistrations: [
      {
        id: 1,
        checkInTime: '2025/07/17 08:40',
        visitorName: '高橋健一',
        company: 'DEF建設株式会社',
        contactPerson: '田村次郎',
        department: '設計部',
        purpose: '新オフィス設計打ち合わせ',
        status: '受付済み'
      },
      {
        id: 2,
        checkInTime: '2025/07/17 07:10',
        visitorName: '田中太郎',
        company: '株式会社ABC商事',
        contactPerson: '山田花子',
        department: '営業部',
        purpose: '新サービスの提案・打ち合わせ',
        status: '面会中'
      },
      {
        id: 3,
        checkInTime: '2025/07/17 05:10',
        visitorName: '佐藤美香',
        company: 'XYZ技術研究所',
        contactPerson: '鈴木一郎',
        department: '技術部',
        purpose: 'セキュリティシステム導入検討',
        status: '連絡済み'
      },
      {
        id: 4,
        checkInTime: '2025/07/17 03:10',
        visitorName: '伊藤真由美',
        company: 'GHI保険会社',
        contactPerson: '中村三郎',
        department: '法務部',
        purpose: '契約内容の確認・更新',
        status: '連絡済み'
      },
      {
        id: 5,
        checkInTime: '2025/07/16 09:10',
        visitorName: '松本裕子',
        company: 'JKL電子株式会社',
        contactPerson: '小林四郎',
        department: 'IT部',
        purpose: 'システム統合に関する相談',
        status: '連絡済み'
      }
    ],
    // 顔認証データタブデータ
    faceAuthData: [
      {
        id: 1,
        visitorName: '田中太郎',
        company: 'ABC商事株式会社',
        email: 'tanaka@abc-trading.co.jp',
        reservationId: 'R001',
        meetingTitle: '新サービス提案会議',
        meetingRoom: '会議室A',
        registrationDate: '2025/07/17 16:30:00',
        expirationDate: '2025/07/18 16:30:00',
        isRegistered: true,
        faceDataSize: '2.4KB',
        lastUsed: '2025/07/17 10:00:00',
        usageCount: 1,
        status: '有効'
      },
      {
        id: 2,
        visitorName: '林由美子',
        company: '株式会社PQR商事',
        email: 'hayashi@pqr-trading.co.jp',
        reservationId: 'R003',
        meetingTitle: 'システム運用相談',
        meetingRoom: '応接室',
        registrationDate: '2025/07/17 14:15:00',
        expirationDate: '2025/07/18 14:15:00',
        isRegistered: true,
        faceDataSize: '2.1KB',
        lastUsed: '2025/07/17 15:15:00',
        usageCount: 1,
        status: '有効'
      },
      {
        id: 3,
        visitorName: '佐藤雄一',
        company: '株式会社テクノロジー',
        email: 'sato@technology.co.jp',
        reservationId: 'R005',
        meetingTitle: 'AI受付システム導入検討',
        meetingRoom: '会議室B',
        registrationDate: '2025/07/16 09:45:00',
        expirationDate: '2025/07/17 09:45:00',
        isRegistered: true,
        faceDataSize: '2.3KB',
        lastUsed: '2025/07/16 14:00:00',
        usageCount: 2,
        status: '期限切れ'
      },
      {
        id: 4,
        visitorName: '山田花子',
        company: 'セキュリティ株式会社',
        email: 'yamada@security.co.jp',
        reservationId: 'R007',
        meetingTitle: 'セキュリティ監査',
        meetingRoom: '大会議室',
        registrationDate: '2025/07/18 08:00:00',
        expirationDate: '2025/07/19 08:00:00',
        isRegistered: true,
        faceDataSize: '2.2KB',
        lastUsed: '-',
        usageCount: 0,
        status: '未使用'
      },
      {
        id: 5,
        visitorName: '小林美咲',
        company: '株式会社イノベーション',
        email: 'kobayashi@innovation.co.jp',
        reservationId: 'R009',
        meetingTitle: 'DX推進プロジェクト',
        meetingRoom: '会議室C',
        registrationDate: '2025/07/15 13:20:00',
        expirationDate: '2025/07/16 13:20:00',
        isRegistered: false,
        faceDataSize: '-',
        lastUsed: '-',
        usageCount: 0,
        status: '登録待ち'
      }
    ]
  })

  // タブの定義
  // プラン別タブ制御
  const baseTabs = [
    { id: 'overview', label: '概要', icon: '📊' },
    { id: 'logs', label: 'ログ管理', icon: '📋' },
    { id: 'users', label: '利用者分析', icon: '👥' },
    { id: 'visitors', label: '来訪者管理', icon: '🏢' },
    { id: 'registrations', label: '来客登録一覧', icon: '📝' },
    { id: 'errors', label: 'エラーログ', icon: '🚨' },
    { id: 'mail', label: 'メール監視', icon: '📧' },
    { id: 'settings', label: '設定', icon: '⚙️' }
  ]

  // プラン別機能タブを追加
  const planSpecificTabs = []
  if (features.faceAuth) {
    planSpecificTabs.push({ id: 'faceauth', label: '顔認証データ', icon: '🔐' })
  }

  const tabs = [...baseTabs, ...planSpecificTabs]

  // タブコンテンツのレンダリング
  const renderTabContent = () => {
    switch (activeTab) {
      case 'overview':
        return renderOverviewTab()
      case 'logs':
        return renderLogsTab()
      case 'prediction':
        return renderPredictionTab()
      case 'users':
        return renderUsersTab()
      case 'visitors':
        return renderVisitorsTab()
      case 'registrations':
        return renderRegistrationsTab()
      case 'faceauth':
        return renderFaceAuthTab()
      case 'errors':
        return renderErrorsTab()
      case 'mail':
        return renderMailTab()
      case 'predictive':
        return renderPredictiveTab()
      case 'settings':
        return renderSettingsTab()
      default:
        return renderOverviewTab()
    }
  }

  // 概要タブ
  const renderOverviewTab = () => (
    <div style={{ display: 'flex', gap: '25px', flexWrap: 'wrap' }}>
      {/* 会議室別利用状況 */}
      <div style={{
        flex: '1 1 45%',
        minWidth: '400px',
        background: 'linear-gradient(135deg, #ffffff 0%, #f8f9fa 100%)',
        borderRadius: '20px',
        padding: '30px',
        boxShadow: '0 12px 40px rgba(0, 0, 0, 0.15), 0 0 0 1px rgba(255, 255, 255, 0.1)',
        border: '1px solid rgba(255, 255, 255, 0.2)',
        backdropFilter: 'blur(10px)'
      }}>
        <h3 style={{ 
          margin: '0 0 25px 0', 
          display: 'flex', 
          alignItems: 'center', 
          gap: '12px',
          fontSize: '20px',
          fontWeight: '700',
          color: '#2d3748',
          textShadow: '0 1px 2px rgba(0, 0, 0, 0.1)'
        }}>
          <span style={{ 
            fontSize: '24px',
            filter: 'drop-shadow(0 2px 4px rgba(0, 0, 0, 0.2))'
          }}>📊</span>
          会議室別利用状況
        </h3>
        <div style={{ display: 'flex', gap: '16px', marginBottom: '30px' }}>
          <div style={{ flex: 1, textAlign: 'center' }}>
            <div style={{ 
              height: '120px', 
              background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)', 
              borderRadius: '12px', 
              margin: '0 0 12px 0', 
              display: 'flex', 
              alignItems: 'end', 
              justifyContent: 'center', 
              paddingBottom: '12px', 
              color: 'white', 
              fontWeight: '700',
              fontSize: '16px',
              boxShadow: '0 8px 25px rgba(102, 126, 234, 0.4)',
              position: 'relative',
              overflow: 'hidden'
            }}>
              <div style={{
                position: 'absolute',
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                background: 'rgba(255, 255, 255, 0.1)',
                backdropFilter: 'blur(10px)'
              }}></div>
              <span style={{ position: 'relative', zIndex: 1 }}>2.0</span>
            </div>
            <div style={{ fontSize: '14px', color: '#4a5568', fontWeight: '600' }}>会議室A</div>
          </div>
          <div style={{ flex: 1, textAlign: 'center' }}>
            <div style={{ 
              height: '120px', 
              background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)', 
              borderRadius: '12px', 
              margin: '0 0 12px 0', 
              display: 'flex', 
              alignItems: 'end', 
              justifyContent: 'center', 
              paddingBottom: '12px', 
              color: 'white', 
              fontWeight: '700',
              fontSize: '16px',
              boxShadow: '0 8px 25px rgba(102, 126, 234, 0.4)',
              position: 'relative',
              overflow: 'hidden'
            }}>
              <div style={{
                position: 'absolute',
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                background: 'rgba(255, 255, 255, 0.1)',
                backdropFilter: 'blur(10px)'
              }}></div>
              <span style={{ position: 'relative', zIndex: 1 }}>2.0</span>
            </div>
            <div style={{ fontSize: '14px', color: '#4a5568', fontWeight: '600' }}>会議室C</div>
          </div>
          <div style={{ flex: 1, textAlign: 'center' }}>
            <div style={{ 
              height: '80px', 
              background: 'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)', 
              borderRadius: '12px', 
              margin: '0 0 12px 0', 
              display: 'flex', 
              alignItems: 'end', 
              justifyContent: 'center', 
              paddingBottom: '12px', 
              color: 'white', 
              fontWeight: '700',
              fontSize: '16px',
              boxShadow: '0 8px 25px rgba(240, 147, 251, 0.4)',
              position: 'relative',
              overflow: 'hidden'
            }}>
              <div style={{
                position: 'absolute',
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                background: 'rgba(255, 255, 255, 0.1)',
                backdropFilter: 'blur(10px)'
              }}></div>
              <span style={{ position: 'relative', zIndex: 1 }}>1.0</span>
            </div>
            <div style={{ fontSize: '14px', color: '#4a5568', fontWeight: '600' }}>会議室B</div>
          </div>
        </div>
        <div style={{ 
          borderTop: '2px solid #e2e8f0', 
          paddingTop: '20px',
          background: 'linear-gradient(135deg, #f7fafc 0%, #edf2f7 100%)',
          borderRadius: '12px',
          padding: '20px',
          margin: '0 -5px'
        }}>
          <div style={{ 
            display: 'flex', 
            justifyContent: 'space-between', 
            alignItems: 'center', 
            marginBottom: '16px',
            padding: '0 8px'
          }}>
            <span style={{ fontSize: '15px', fontWeight: '700', color: '#2d3748' }}>会議室</span>
            <span style={{ fontSize: '15px', fontWeight: '700', color: '#2d3748' }}>予約件数</span>
            <span style={{ fontSize: '15px', fontWeight: '700', color: '#2d3748' }}>平均利用時間</span>
            <span style={{ fontSize: '15px', fontWeight: '700', color: '#2d3748' }}>稼働率</span>
          </div>
          <div style={{ 
            display: 'flex', 
            justifyContent: 'space-between', 
            alignItems: 'center', 
            marginBottom: '12px',
            padding: '12px 8px',
            background: 'rgba(255, 255, 255, 0.8)',
            borderRadius: '8px',
            boxShadow: '0 2px 4px rgba(0, 0, 0, 0.05)'
          }}>
            <span style={{ fontWeight: '600', color: '#4a5568' }}>会議室A</span>
            <span style={{ 
              color: '#38a169', 
              fontWeight: '700',
              background: 'rgba(56, 161, 105, 0.1)',
              padding: '4px 8px',
              borderRadius: '6px'
            }}>2件</span>
            <span style={{ 
              color: '#dd6b20', 
              fontWeight: '700',
              background: 'rgba(221, 107, 32, 0.1)',
              padding: '4px 8px',
              borderRadius: '6px'
            }}>2.5時間</span>
            <span style={{ fontWeight: '600', color: '#4a5568' }}>2/2</span>
          </div>
          <div style={{ 
            display: 'flex', 
            justifyContent: 'space-between', 
            alignItems: 'center',
            padding: '12px 8px',
            background: 'rgba(255, 255, 255, 0.8)',
            borderRadius: '8px',
            boxShadow: '0 2px 4px rgba(0, 0, 0, 0.05)'
          }}>
            <span style={{ fontWeight: '600', color: '#4a5568' }}>会議室C</span>
            <span style={{ 
              color: '#38a169', 
              fontWeight: '700',
              background: 'rgba(56, 161, 105, 0.1)',
              padding: '4px 8px',
              borderRadius: '6px'
            }}>2件</span>
            <span style={{ 
              color: '#dd6b20', 
              fontWeight: '700',
              background: 'rgba(221, 107, 32, 0.1)',
              padding: '4px 8px',
              borderRadius: '6px'
            }}>1.3時間</span>
            <span style={{ fontWeight: '600', color: '#4a5568' }}>1/2</span>
          </div>
        </div>
      </div>

      {/* 平日/週末別利用状況 */}
      <div style={{
        flex: '1 1 45%',
        minWidth: '400px',
        background: 'linear-gradient(135deg, #ffffff 0%, #f8f9fa 100%)',
        borderRadius: '20px',
        padding: '30px',
        boxShadow: '0 12px 40px rgba(0, 0, 0, 0.15), 0 0 0 1px rgba(255, 255, 255, 0.1)',
        border: '1px solid rgba(255, 255, 255, 0.2)',
        backdropFilter: 'blur(10px)'
      }}>
        <h3 style={{ margin: '0 0 20px 0', display: 'flex', alignItems: 'center', gap: '8px' }}>
          📊 平日/週末別利用状況
        </h3>
        <div style={{ borderTop: '1px solid #eee', paddingTop: '16px' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
            <span style={{ fontSize: '14px', fontWeight: 'bold' }}>期間</span>
            <span style={{ fontSize: '14px', fontWeight: 'bold' }}>予約件数</span>
            <span style={{ fontSize: '14px', fontWeight: 'bold' }}>平均利用時間</span>
          </div>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
            <span>平日</span>
            <span>4件</span>
            <span>1.9時間</span>
          </div>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <span>週末</span>
            <span>1件</span>
            <span>2.0時間</span>
          </div>
        </div>
      </div>

      {/* 月別予約推移 */}
      <div style={{
        flex: '1 1 45%',
        minWidth: '400px',
        background: 'white',
        borderRadius: '12px',
        padding: '24px',
        boxShadow: '0 2px 8px rgba(0,0,0,0.1)'
      }}>
        <h3 style={{ margin: '0 0 20px 0', display: 'flex', alignItems: 'center', gap: '8px' }}>
          📊 月別予約推移
        </h3>
        <div style={{ borderTop: '1px solid #eee', paddingTop: '16px' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
            <span style={{ fontSize: '14px', fontWeight: 'bold' }}>月</span>
            <span style={{ fontSize: '14px', fontWeight: 'bold' }}>予約件数</span>
            <span style={{ fontSize: '14px', fontWeight: 'bold' }}>利用会議室数</span>
            <span style={{ fontSize: '14px', fontWeight: 'bold' }}>平均利用時間</span>
          </div>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <span>2025-07</span>
            <span>5件</span>
            <span>3室</span>
            <span>1.9時間</span>
          </div>
        </div>
      </div>
    </div>
  )

  // ログ管理タブ
  const renderLogsTab = () => (
    <div style={{ 
      background: 'linear-gradient(135deg, #ffffff 0%, #f8f9fa 100%)', 
      borderRadius: '20px', 
      padding: '30px', 
      boxShadow: '0 12px 40px rgba(0, 0, 0, 0.15), 0 0 0 1px rgba(255, 255, 255, 0.1)',
      border: '1px solid rgba(255, 255, 255, 0.2)',
      backdropFilter: 'blur(10px)'
    }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
        <h3 style={{ 
          margin: 0, 
          display: 'flex', 
          alignItems: 'center', 
          gap: '12px',
          fontSize: '20px',
          fontWeight: '700',
          color: '#2d3748',
          textShadow: '0 1px 2px rgba(0, 0, 0, 0.1)'
        }}>
          <span style={{ 
            fontSize: '24px',
            filter: 'drop-shadow(0 2px 4px rgba(0, 0, 0, 0.2))'
          }}>📋</span>
          ログ管理
        </h3>
        <div style={{ display: 'flex', gap: '10px' }}>
          <button style={{ padding: '8px 16px', background: '#32CD32', color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer' }}>
            📊 更新
          </button>
          <button style={{ padding: '8px 16px', background: '#FF8C00', color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer' }}>
            📁 CSV出力
          </button>
        </div>
      </div>
      
      <div style={{ display: 'flex', gap: '20px', marginBottom: '20px' }}>
        <div>
          <label style={{ display: 'block', marginBottom: '4px', fontSize: '14px' }}>開始日:</label>
          <input type="date" value="2025-07-10" style={{ padding: '8px', border: '1px solid #ccc', borderRadius: '4px' }} />
        </div>
        <div>
          <label style={{ display: 'block', marginBottom: '4px', fontSize: '14px' }}>終了日:</label>
          <input type="date" value="2025-07-17" style={{ padding: '8px', border: '1px solid #ccc', borderRadius: '4px' }} />
        </div>
        <div>
          <label style={{ display: 'block', marginBottom: '4px', fontSize: '14px' }}>会議室でフィルタ:</label>
          <select style={{ padding: '8px', border: '1px solid #ccc', borderRadius: '4px' }}>
            <option>すべて</option>
            <option>会議室A</option>
            <option>会議室B</option>
            <option>会議室C</option>
          </select>
        </div>
        {features.faceAuth && (
          <div>
            <label style={{ display: 'block', marginBottom: '4px', fontSize: '14px' }}>顔認証:</label>
            <select style={{ padding: '8px', border: '1px solid #ccc', borderRadius: '4px' }}>
              <option>すべて</option>
              <option>認証済み</option>
              <option>未認証</option>
            </select>
          </div>
        )}
        <div>
          <label style={{ display: 'block', marginBottom: '4px', fontSize: '14px' }}>来訪者名で検索:</label>
          <input type="text" placeholder="名前を入力..." style={{ padding: '8px', border: '1px solid #ccc', borderRadius: '4px' }} />
        </div>
      </div>

      <div style={{ overflowX: 'auto' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
          <thead>
            <tr style={{ background: '#f8f9fa', borderBottom: '2px solid #e9ecef' }}>
              <th style={{ padding: '12px', textAlign: 'left', fontWeight: 'bold' }}>チェックイン時刻</th>
              <th style={{ padding: '12px', textAlign: 'left', fontWeight: 'bold' }}>来訪者名</th>
              <th style={{ padding: '12px', textAlign: 'left', fontWeight: 'bold' }}>会社名</th>
              <th style={{ padding: '12px', textAlign: 'left', fontWeight: 'bold' }}>会議</th>
              <th style={{ padding: '12px', textAlign: 'left', fontWeight: 'bold' }}>会議室</th>
              {features.faceAuth && <th style={{ padding: '12px', textAlign: 'left', fontWeight: 'bold' }}>顔認証</th>}
              <th style={{ padding: '12px', textAlign: 'left', fontWeight: 'bold' }}>通知</th>
              <th style={{ padding: '12px', textAlign: 'left', fontWeight: 'bold' }}>操作</th>
            </tr>
          </thead>
          <tbody>
            {demoData.logManagement.map((log, index) => (
              <tr key={log.id} style={{ borderBottom: '1px solid #e9ecef' }}>
                <td style={{ padding: '12px' }}>{log.timestamp}</td>
                <td style={{ padding: '12px' }}>{log.visitorName}</td>
                <td style={{ padding: '12px' }}>{log.company}</td>
                <td style={{ padding: '12px' }}>-</td>
                <td style={{ padding: '12px' }}>{log.meetingRoom}</td>
                {features.faceAuth && (
                  <td style={{ padding: '12px' }}>
                    {log.faceAuth ? (
                      <span style={{ color: '#32CD32' }}>✓</span>
                    ) : (
                      <span style={{ color: '#FF6B6B' }}>✗</span>
                    )}
                  </td>
                )}
                <td style={{ padding: '12px' }}>
                  {log.notification ? (
                    <span style={{ color: '#32CD32' }}>✓</span>
                  ) : (
                    <span style={{ color: '#FF6B6B' }}>✗</span>
                  )}
                </td>
                <td style={{ padding: '12px' }}>
                  <button style={{ padding: '4px 8px', background: '#dc3545', color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer', fontSize: '12px' }}>
                    🗑
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  )

  // 予測分析タブ
  const renderPredictionTab = () => (
    <div style={{ background: 'white', borderRadius: '12px', padding: '24px', boxShadow: '0 2px 8px rgba(0,0,0,0.1)' }}>
      <div style={{ 
        background: 'linear-gradient(135deg, #6B73FF 0%, #9B59B6 100%)',
        color: 'white',
        padding: '20px',
        borderRadius: '12px',
        marginBottom: '20px'
      }}>
        <h2 style={{ margin: 0, display: 'flex', alignItems: 'center', gap: '10px' }}>
          📊 予測分析ダッシュボード
        </h2>
        <p style={{ margin: '5px 0 0 0', opacity: 0.9 }}>
          AIによる来訪者・会議室・セキュリティの予測分析
        </p>
        <div style={{ float: 'right', marginTop: '-40px' }}>
          <select style={{ padding: '8px 12px', borderRadius: '6px', border: 'none', background: 'rgba(255,255,255,0.2)', color: 'white' }}>
            <option value="1week">1週間</option>
            <option value="1month">1ヶ月</option>
            <option value="3months">3ヶ月</option>
          </select>
        </div>
      </div>
      
      <div style={{ display: 'flex', gap: '20px', flexWrap: 'wrap' }}>
        {/* 来訪者トレンド予測 */}
        <div style={{ flex: '1 1 45%', minWidth: '400px' }}>
          <h3 style={{ margin: '0 0 16px 0', display: 'flex', alignItems: 'center', gap: '8px' }}>
            📈 来訪者トレンド予測
          </h3>
          <div style={{ fontSize: '12px', color: '#666', marginBottom: '16px' }}>
            実績と予測の比較
          </div>
          <div style={{ height: '200px', background: '#f8f9fa', borderRadius: '8px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <div style={{ textAlign: 'center' }}>
              <div style={{ fontSize: '24px', marginBottom: '8px' }}>📊</div>
              <div>来訪者数予測グラフ</div>
              <div style={{ fontSize: '12px', color: '#666', marginTop: '8px' }}>
                予測結果: 来週は245名の来訪者が予想されます（信頼度: 87%）
              </div>
            </div>
          </div>
        </div>

        {/* 会議室利用率予測 */}
        <div style={{ flex: '1 1 45%', minWidth: '400px' }}>
          <h3 style={{ margin: '0 0 16px 0', display: 'flex', alignItems: 'center', gap: '8px' }}>
            🏢 会議室利用率予測
          </h3>
          <div style={{ fontSize: '12px', color: '#666', marginBottom: '16px' }}>
            現在と予測の利用率比較
          </div>
          <div style={{ display: 'flex', gap: '10px' }}>
            <div style={{ flex: 1, textAlign: 'center' }}>
              <div style={{ height: '80px', background: '#4169E1', borderRadius: '4px', marginBottom: '8px' }}></div>
              <div style={{ fontSize: '12px' }}>会議室A</div>
              <div style={{ fontSize: '10px', color: '#666' }}>現在: 85%</div>
              <div style={{ fontSize: '10px', color: '#666' }}>予測: 90%</div>
            </div>
            <div style={{ flex: 1, textAlign: 'center' }}>
              <div style={{ height: '70px', background: '#32CD32', borderRadius: '4px', marginBottom: '8px' }}></div>
              <div style={{ fontSize: '12px' }}>会議室B</div>
              <div style={{ fontSize: '10px', color: '#666' }}>現在: 75%</div>
              <div style={{ fontSize: '10px', color: '#666' }}>予測: 80%</div>
            </div>
            <div style={{ flex: 1, textAlign: 'center' }}>
              <div style={{ height: '85px', background: '#FF8C00', borderRadius: '4px', marginBottom: '8px' }}></div>
              <div style={{ fontSize: '12px' }}>会議室C</div>
              <div style={{ fontSize: '10px', color: '#666' }}>現在: 90%</div>
              <div style={{ fontSize: '10px', color: '#666' }}>予測: 85%</div>
            </div>
            <div style={{ flex: 1, textAlign: 'center' }}>
              <div style={{ height: '60px', background: '#A0522D', borderRadius: '4px', marginBottom: '8px' }}></div>
              <div style={{ fontSize: '12px' }}>大会議室</div>
              <div style={{ fontSize: '10px', color: '#666' }}>現在: 65%</div>
              <div style={{ fontSize: '10px', color: '#666' }}>予測: 70%</div>
            </div>
            <div style={{ flex: 1, textAlign: 'center' }}>
              <div style={{ height: '40px', background: '#DA70D6', borderRadius: '4px', marginBottom: '8px' }}></div>
              <div style={{ fontSize: '12px' }}>VIP会議室</div>
              <div style={{ fontSize: '10px', color: '#666' }}>現在: 45%</div>
              <div style={{ fontSize: '10px', color: '#666' }}>予測: 50%</div>
            </div>
          </div>
        </div>

        {/* ピーク時間分析 */}
        <div style={{ flex: '1 1 45%', minWidth: '400px' }}>
          <h3 style={{ margin: '0 0 16px 0', display: 'flex', alignItems: 'center', gap: '8px' }}>
            ⏰ ピーク時間分析
          </h3>
          <div style={{ fontSize: '12px', color: '#666', marginBottom: '16px' }}>
            時間帯別来訪者数予測
          </div>
          <div style={{ height: '200px', background: '#f8f9fa', borderRadius: '8px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <div style={{ textAlign: 'center' }}>
              <div style={{ fontSize: '24px', marginBottom: '8px' }}>📊</div>
              <div>時間帯別利用状況</div>
              <div style={{ fontSize: '12px', color: '#666', marginTop: '8px' }}>
                平日来訪者数 vs 予測来訪者数
              </div>
            </div>
          </div>
        </div>

        {/* セキュリティアラート分析 */}
        <div style={{ flex: '1 1 45%', minWidth: '400px' }}>
          <h3 style={{ margin: '0 0 16px 0', display: 'flex', alignItems: 'center', gap: '8px' }}>
            🔒 セキュリティアラート分析
          </h3>
          <div style={{ fontSize: '12px', color: '#666', marginBottom: '16px' }}>
            アラートタイプ別分析
          </div>
          <div style={{ height: '100px', background: '#f8f9fa', borderRadius: '8px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <div style={{ textAlign: 'center' }}>
              <div style={{ fontSize: '24px', marginBottom: '8px' }}>🚨</div>
              <div>セキュリティアラート分析</div>
            </div>
          </div>
        </div>

        {/* 予測サマリー・推奨事項 */}
        <div style={{ flex: '1 1 45%', minWidth: '400px' }}>
          <h3 style={{ margin: '0 0 16px 0', display: 'flex', alignItems: 'center', gap: '8px' }}>
            📋 予測サマリー・推奨事項
          </h3>
          <div style={{ height: '100px', background: '#f8f9fa', borderRadius: '8px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <div style={{ textAlign: 'center' }}>
              <div style={{ fontSize: '24px', marginBottom: '8px' }}>📈</div>
              <div>予測サマリー・推奨事項</div>
            </div>
          </div>
        </div>
      </div>
    </div>
  )

  // 利用者分析タブ
  const renderUsersTab = () => (
    <div style={{ display: 'flex', gap: '20px', flexWrap: 'wrap' }}>
      {/* 主催者別予約ランキング */}
      <div style={{
        flex: '1 1 30%',
        minWidth: '300px',
        background: 'white',
        borderRadius: '12px',
        padding: '24px',
        boxShadow: '0 2px 8px rgba(0,0,0,0.1)'
      }}>
        <h3 style={{ margin: '0 0 20px 0', display: 'flex', alignItems: 'center', gap: '8px' }}>
          👥 主催者別予約ランキング
        </h3>
        <div style={{ display: 'flex', gap: '5px', marginBottom: '20px' }}>
          {demoData.userAnalysis.organizerRanking.map((organizer, index) => (
            <div key={index} style={{ flex: 1, textAlign: 'center' }}>
              <div style={{ height: '80px', background: '#32CD32', borderRadius: '4px', margin: '0 0 8px 0' }}></div>
              <div style={{ fontSize: '10px', color: '#666' }}>{organizer.name}</div>
              <div style={{ fontSize: '10px', color: '#666' }}>{organizer.department}</div>
            </div>
          ))}
        </div>
        <div style={{ borderTop: '1px solid #eee', paddingTop: '16px' }}>
          <table style={{ width: '100%', fontSize: '12px' }}>
            <thead>
              <tr style={{ borderBottom: '1px solid #eee' }}>
                <th style={{ padding: '8px', textAlign: 'left' }}>主催者</th>
                <th style={{ padding: '8px', textAlign: 'left' }}>予約件数</th>
                <th style={{ padding: '8px', textAlign: 'left' }}>平均利用時間</th>
                <th style={{ padding: '8px', textAlign: 'left' }}>利用会議室数</th>
                <th style={{ padding: '8px', textAlign: 'left' }}>承認率</th>
              </tr>
            </thead>
            <tbody>
              {demoData.userAnalysis.organizerRanking.map((organizer, index) => (
                <tr key={index} style={{ borderBottom: '1px solid #f0f0f0' }}>
                  <td style={{ padding: '8px' }}>{organizer.name}</td>
                  <td style={{ padding: '8px' }}>{organizer.bookings}</td>
                  <td style={{ padding: '8px' }}>{organizer.avgDuration}</td>
                  <td style={{ padding: '8px' }}>{organizer.usage}</td>
                  <td style={{ padding: '8px' }}>{organizer.approvalRate}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* 部署別利用統計 */}
      <div style={{
        flex: '1 1 30%',
        minWidth: '300px',
        background: 'white',
        borderRadius: '12px',
        padding: '24px',
        boxShadow: '0 2px 8px rgba(0,0,0,0.1)'
      }}>
        <h3 style={{ margin: '0 0 20px 0', display: 'flex', alignItems: 'center', gap: '8px' }}>
          🏢 部署別利用統計
        </h3>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', marginBottom: '20px' }}>
          <div style={{ width: '150px', height: '150px', borderRadius: '50%', background: 'conic-gradient(#4169E1 0% 25%, #32CD32 25% 55%, #FF8C00 55% 75%, #A0522D 75% 90%, #DA70D6 90% 100%)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <div style={{ width: '80px', height: '80px', borderRadius: '50%', background: 'white' }}></div>
          </div>
        </div>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '10px', justifyContent: 'center' }}>
          {Object.entries(demoData.userAnalysis.departmentStats).map(([dept, data]) => (
            <div key={dept} style={{ display: 'flex', alignItems: 'center', gap: '4px', fontSize: '12px' }}>
              <div style={{ width: '12px', height: '12px', background: data.color, borderRadius: '2px' }}></div>
              <span>{dept}</span>
            </div>
          ))}
        </div>
        <div style={{ borderTop: '1px solid #eee', paddingTop: '16px', marginTop: '20px' }}>
          <table style={{ width: '100%', fontSize: '12px' }}>
            <thead>
              <tr style={{ borderBottom: '1px solid #eee' }}>
                <th style={{ padding: '8px', textAlign: 'left' }}>部署</th>
                <th style={{ padding: '8px', textAlign: 'left' }}>予約件数</th>
                <th style={{ padding: '8px', textAlign: 'left' }}>平均利用時間</th>
                <th style={{ padding: '8px', textAlign: 'left' }}>割合</th>
              </tr>
            </thead>
            <tbody>
              {demoData.userAnalysis.departmentDetails.map((dept, index) => (
                <tr key={index} style={{ borderBottom: '1px solid #f0f0f0' }}>
                  <td style={{ padding: '8px' }}>{dept.department}</td>
                  <td style={{ padding: '8px', color: '#32CD32' }}>{dept.bookings}件</td>
                  <td style={{ padding: '8px', color: '#FF8C00' }}>{dept.avgDuration}</td>
                  <td style={{ padding: '8px' }}>{dept.percentage}%</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* 利用頻度分析 */}
      <div style={{
        flex: '1 1 30%',
        minWidth: '300px',
        background: 'white',
        borderRadius: '12px',
        padding: '24px',
        boxShadow: '0 2px 8px rgba(0,0,0,0.1)'
      }}>
        <h3 style={{ margin: '0 0 20px 0', display: 'flex', alignItems: 'center', gap: '8px' }}>
          📊 利用頻度分析
        </h3>
        <div style={{ borderTop: '1px solid #eee', paddingTop: '16px' }}>
          <table style={{ width: '100%', fontSize: '12px' }}>
            <thead>
              <tr style={{ borderBottom: '1px solid #eee' }}>
                <th style={{ padding: '8px', textAlign: 'left' }}>ユーザータイプ</th>
                <th style={{ padding: '8px', textAlign: 'left' }}>ユーザー数</th>
                <th style={{ padding: '8px', textAlign: 'left' }}>割合</th>
              </tr>
            </thead>
            <tbody>
              {Object.entries(demoData.userAnalysis.userTypes).map(([type, data]) => (
                <tr key={type} style={{ borderBottom: '1px solid #f0f0f0' }}>
                  <td style={{ padding: '8px' }}>{type}</td>
                  <td style={{ padding: '8px' }}>{data.count}人</td>
                  <td style={{ padding: '8px' }}>{data.percentage}%</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  )

  // 来訪者管理タブ
  const renderVisitorsTab = () => (
    <div style={{ background: 'white', borderRadius: '12px', padding: '24px', boxShadow: '0 2px 8px rgba(0,0,0,0.1)' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
        <h3 style={{ margin: 0, display: 'flex', alignItems: 'center', gap: '8px' }}>
          🏢 来訪者管理
        </h3>
        <div style={{ display: 'flex', gap: '10px' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '5px' }}>
            <span style={{ fontSize: '12px' }}>📅 日付:</span>
            <input type="date" value="2025-07-17" style={{ padding: '4px', border: '1px solid #ccc', borderRadius: '4px', fontSize: '12px' }} />
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '5px' }}>
            <span style={{ fontSize: '12px' }}>🔍 ステータス:</span>
            <select style={{ padding: '4px', border: '1px solid #ccc', borderRadius: '4px', fontSize: '12px' }}>
              <option>すべて</option>
              <option>予定</option>
              <option>確認済み</option>
              <option>完了</option>
            </select>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '5px' }}>
            <span style={{ fontSize: '12px' }}>📊 継続中:</span>
            <button style={{ padding: '4px 8px', background: '#32CD32', color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer', fontSize: '12px' }}>
              5
            </button>
          </div>
          <button style={{ padding: '4px 8px', background: '#007bff', color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer', fontSize: '12px' }}>
            🔄 全て折りたたみ
          </button>
        </div>
      </div>
      
      <div style={{ display: 'flex', gap: '20px', flexWrap: 'wrap' }}>
        {demoData.visitorManagement.currentVisitors.map((visitor, index) => (
          <div key={visitor.id} style={{
            flex: '1 1 300px',
            border: '1px solid #ddd',
            borderRadius: '8px',
            padding: '16px',
            background: '#f8f9fa'
          }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '12px' }}>
              <div>
                <h4 style={{ margin: '0 0 4px 0', fontSize: '16px' }}>{visitor.name}</h4>
                <p style={{ margin: '0', fontSize: '12px', color: '#666' }}>{visitor.company}</p>
                <div style={{ display: 'flex', alignItems: 'center', gap: '4px', marginTop: '8px' }}>
                  <span style={{ fontSize: '12px', color: '#666' }}>⏰ 通常</span>
                  <span style={{ 
                    background: visitor.status === '予定' ? '#007bff' : '#28a745',
                    color: 'white',
                    padding: '2px 6px',
                    borderRadius: '4px',
                    fontSize: '10px'
                  }}>
                    {visitor.status}
                  </span>
                </div>
              </div>
              <div style={{ display: 'flex', gap: '4px' }}>
                <span style={{ fontSize: '12px', color: '#666' }}>🔽 詳細を非表示</span>
              </div>
            </div>
            
            <div style={{ fontSize: '12px', color: '#333' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '4px' }}>
                <span style={{ color: '#666' }}>👤 担当者:</span>
                <span>{visitor.contactPerson} ({visitor.department})</span>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '4px' }}>
                <span style={{ color: '#666' }}>📧 メール:</span>
                <span>{visitor.email}</span>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '4px' }}>
                <span style={{ color: '#666' }}>📞 電話:</span>
                <span>{visitor.phone}</span>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '4px' }}>
                <span style={{ color: '#666' }}>⏰ 時間:</span>
                <span>{visitor.time} ({visitor.duration})</span>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '4px' }}>
                <span style={{ color: '#666' }}>🏢 会議室:</span>
                <span>{visitor.meetingRoom}</span>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '4px' }}>
                <span style={{ color: '#666' }}>🎯 目的:</span>
                <span>{visitor.purpose}</span>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '4px' }}>
                <span style={{ color: '#666' }}>👥 同行者:</span>
                <span>{visitor.attendees}</span>
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  )

  // 来客登録一覧タブ
  const renderRegistrationsTab = () => (
    <div style={{ 
      background: 'linear-gradient(135deg, #ffffff 0%, #f8f9fa 100%)', 
      borderRadius: '20px', 
      padding: '30px', 
      boxShadow: '0 12px 40px rgba(0, 0, 0, 0.15), 0 0 0 1px rgba(255, 255, 255, 0.1)',
      border: '1px solid rgba(255, 255, 255, 0.2)',
      backdropFilter: 'blur(10px)'
    }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
        <h3 style={{ 
          margin: 0, 
          display: 'flex', 
          alignItems: 'center', 
          gap: '12px',
          fontSize: '20px',
          fontWeight: '700',
          color: '#2d3748',
          textShadow: '0 1px 2px rgba(0, 0, 0, 0.1)'
        }}>
          <span style={{ 
            fontSize: '24px',
            filter: 'drop-shadow(0 2px 4px rgba(0, 0, 0, 0.2))'
          }}>👥</span>
          来客登録一覧
        </h3>
        <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '5px' }}>
            <span style={{ fontSize: '12px' }}>来訪者名、会社名、担当者で検索:</span>
            <input type="text" placeholder="全て" style={{ padding: '4px 8px', border: '1px solid #ccc', borderRadius: '4px', fontSize: '12px', width: '120px' }} />
          </div>
          <button style={{ padding: '4px 8px', background: '#007bff', color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer', fontSize: '12px' }}>
            🔍
          </button>
          <div style={{ display: 'flex', gap: '8px' }}>
            <button style={{ 
              padding: '8px 16px', 
              background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)', 
              color: 'white', 
              border: 'none', 
              borderRadius: '8px', 
              cursor: 'pointer', 
              fontSize: '13px',
              fontWeight: '600',
              boxShadow: '0 4px 12px rgba(102, 126, 234, 0.4)',
              transition: 'all 0.3s ease'
            }}>
              継続中 5
            </button>
            <button style={{ 
              padding: '8px 16px', 
              background: 'linear-gradient(135deg, #48bb78 0%, #38a169 100%)', 
              color: 'white', 
              border: 'none', 
              borderRadius: '8px', 
              cursor: 'pointer', 
              fontSize: '13px',
              fontWeight: '600',
              boxShadow: '0 4px 12px rgba(72, 187, 120, 0.4)',
              transition: 'all 0.3s ease'
            }}>
              受付済み 1
            </button>
            <button style={{ 
              padding: '8px 16px', 
              background: 'linear-gradient(135deg, #4299e1 0%, #3182ce 100%)', 
              color: 'white', 
              border: 'none', 
              borderRadius: '8px', 
              cursor: 'pointer', 
              fontSize: '13px',
              fontWeight: '600',
              boxShadow: '0 4px 12px rgba(66, 153, 225, 0.4)',
              transition: 'all 0.3s ease'
            }}>
              面会中 1
            </button>
            <button style={{ 
              padding: '8px 16px', 
              background: 'linear-gradient(135deg, #a0aec0 0%, #718096 100%)', 
              color: 'white', 
              border: 'none', 
              borderRadius: '8px', 
              cursor: 'pointer', 
              fontSize: '13px',
              fontWeight: '600',
              boxShadow: '0 4px 12px rgba(160, 174, 192, 0.4)',
              transition: 'all 0.3s ease'
            }}>
              連絡済み 3
            </button>
          </div>
        </div>
      </div>
      
      <div style={{ overflowX: 'auto' }}>
        <table style={{ 
          width: '100%', 
          borderCollapse: 'collapse',
          borderRadius: '12px',
          overflow: 'hidden',
          boxShadow: '0 8px 25px rgba(0, 0, 0, 0.15)'
        }}>
          <thead>
            <tr style={{ 
              background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)', 
              color: 'white' 
            }}>
              <th style={{ 
                padding: '16px 12px', 
                textAlign: 'left', 
                fontWeight: '700',
                fontSize: '14px',
                textShadow: '0 1px 2px rgba(0, 0, 0, 0.2)'
              }}>来訪日時</th>
              <th style={{ 
                padding: '16px 12px', 
                textAlign: 'left', 
                fontWeight: '700',
                fontSize: '14px',
                textShadow: '0 1px 2px rgba(0, 0, 0, 0.2)'
              }}>来訪者名</th>
              <th style={{ 
                padding: '16px 12px', 
                textAlign: 'left', 
                fontWeight: '700',
                fontSize: '14px',
                textShadow: '0 1px 2px rgba(0, 0, 0, 0.2)'
              }}>会社名</th>
              <th style={{ 
                padding: '16px 12px', 
                textAlign: 'left', 
                fontWeight: '700',
                fontSize: '14px',
                textShadow: '0 1px 2px rgba(0, 0, 0, 0.2)'
              }}>担当者</th>
              <th style={{ 
                padding: '16px 12px', 
                textAlign: 'left', 
                fontWeight: '700',
                fontSize: '14px',
                textShadow: '0 1px 2px rgba(0, 0, 0, 0.2)'
              }}>部署</th>
              <th style={{ 
                padding: '16px 12px', 
                textAlign: 'left', 
                fontWeight: '700',
                fontSize: '14px',
                textShadow: '0 1px 2px rgba(0, 0, 0, 0.2)'
              }}>目的</th>
              <th style={{ 
                padding: '16px 12px', 
                textAlign: 'left', 
                fontWeight: '700',
                fontSize: '14px',
                textShadow: '0 1px 2px rgba(0, 0, 0, 0.2)'
              }}>ステータス</th>
              <th style={{ 
                padding: '16px 12px', 
                textAlign: 'left', 
                fontWeight: '700',
                fontSize: '14px',
                textShadow: '0 1px 2px rgba(0, 0, 0, 0.2)'
              }}>操作</th>
            </tr>
          </thead>
          <tbody>
            {demoData.visitorRegistrations.map((registration, index) => (
              <tr key={registration.id} style={{ 
                borderBottom: '2px solid #e2e8f0', 
                background: index % 2 === 0 
                  ? 'linear-gradient(135deg, #f7fafc 0%, #edf2f7 100%)' 
                  : 'rgba(255, 255, 255, 0.8)',
                transition: 'all 0.3s ease'
              }}>
                <td style={{ 
                  padding: '16px 12px',
                  fontWeight: '600',
                  color: '#4a5568'
                }}>{registration.checkInTime}</td>
                <td style={{ 
                  padding: '16px 12px', 
                  color: '#667eea', 
                  textDecoration: 'underline', 
                  cursor: 'pointer',
                  fontWeight: '600'
                }}>{registration.visitorName}</td>
                <td style={{ 
                  padding: '16px 12px',
                  color: '#4a5568',
                  fontWeight: '500'
                }}>{registration.company}</td>
                <td style={{ 
                  padding: '16px 12px',
                  color: '#4a5568',
                  fontWeight: '500'
                }}>{registration.contactPerson}</td>
                <td style={{ 
                  padding: '16px 12px',
                  color: '#4a5568',
                  fontWeight: '500'
                }}>{registration.department}</td>
                <td style={{ 
                  padding: '16px 12px',
                  color: '#4a5568',
                  fontWeight: '500'
                }}>{registration.purpose}</td>
                <td style={{ padding: '16px 12px' }}>
                  <select style={{ 
                    padding: '8px 12px', 
                    border: 'none', 
                    borderRadius: '8px',
                    background: registration.status === '受付済み' 
                      ? 'linear-gradient(135deg, #48bb78 0%, #38a169 100%)' 
                      : registration.status === '面会中' 
                      ? 'linear-gradient(135deg, #4299e1 0%, #3182ce 100%)' 
                      : 'linear-gradient(135deg, #a0aec0 0%, #718096 100%)',
                    color: 'white',
                    fontSize: '13px',
                    fontWeight: '600',
                    boxShadow: '0 2px 8px rgba(0, 0, 0, 0.15)',
                    cursor: 'pointer'
                  }}>
                    <option>{registration.status}</option>
                  </select>
                </td>
                <td style={{ padding: '16px 12px' }}>
                  <button style={{ 
                    padding: '8px 12px', 
                    background: 'linear-gradient(135deg, #f56565 0%, #e53e3e 100%)', 
                    color: 'white', 
                    border: 'none', 
                    borderRadius: '8px', 
                    cursor: 'pointer', 
                    fontSize: '13px',
                    fontWeight: '600',
                    boxShadow: '0 2px 8px rgba(245, 101, 101, 0.4)',
                    transition: 'all 0.3s ease'
                  }}>
                    🗑
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  )

  // 顔認証データタブ
  const renderFaceAuthTab = () => (
    <div style={{ background: 'white', borderRadius: '12px', padding: '24px', boxShadow: '0 2px 8px rgba(0,0,0,0.1)' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
        <h3 style={{ margin: 0, display: 'flex', alignItems: 'center', gap: '8px' }}>
          🔐 顔認証データ管理
        </h3>
        <div style={{ display: 'flex', gap: '12px' }}>
          <button 
            onClick={() => performMaintenance('cleanup')}
            style={{
              padding: '8px 16px',
              backgroundColor: '#dc3545',
              color: 'white',
              border: 'none',
              borderRadius: '6px',
              cursor: 'pointer',
              fontSize: '14px',
              fontWeight: '500'
            }}
          >
            期限切れデータ削除
          </button>
          <button 
            onClick={() => alert('顔認証データのエクスポート機能は準備中です')}
            style={{
              padding: '8px 16px',
              backgroundColor: '#28a745',
              color: 'white',
              border: 'none',
              borderRadius: '6px',
              cursor: 'pointer',
              fontSize: '14px',
              fontWeight: '500'
            }}
          >
            データエクスポート
          </button>
        </div>
      </div>
      
      <div style={{ marginBottom: '20px' }}>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '16px' }}>
          <div style={{ 
            padding: '16px', 
            backgroundColor: '#e3f2fd', 
            borderRadius: '8px',
            border: '1px solid #bbdefb'
          }}>
            <div style={{ fontSize: '24px', fontWeight: 'bold', color: '#1976d2' }}>
              {demoData.faceAuthData.filter(item => item.status === '有効').length}
            </div>
            <div style={{ color: '#1976d2', fontSize: '14px' }}>有効なデータ</div>
          </div>
          <div style={{ 
            padding: '16px', 
            backgroundColor: '#fff3e0', 
            borderRadius: '8px',
            border: '1px solid #ffcc02'
          }}>
            <div style={{ fontSize: '24px', fontWeight: 'bold', color: '#f57c00' }}>
              {demoData.faceAuthData.filter(item => item.status === '未使用').length}
            </div>
            <div style={{ color: '#f57c00', fontSize: '14px' }}>未使用データ</div>
          </div>
          <div style={{ 
            padding: '16px', 
            backgroundColor: '#ffebee', 
            borderRadius: '8px',
            border: '1px solid #ffcdd2'
          }}>
            <div style={{ fontSize: '24px', fontWeight: 'bold', color: '#d32f2f' }}>
              {demoData.faceAuthData.filter(item => item.status === '期限切れ').length}
            </div>
            <div style={{ color: '#d32f2f', fontSize: '14px' }}>期限切れデータ</div>
          </div>
          <div style={{ 
            padding: '16px', 
            backgroundColor: '#f3e5f5', 
            borderRadius: '8px',
            border: '1px solid #e1bee7'
          }}>
            <div style={{ fontSize: '24px', fontWeight: 'bold', color: '#7b1fa2' }}>
              {demoData.faceAuthData.filter(item => item.status === '登録待ち').length}
            </div>
            <div style={{ color: '#7b1fa2', fontSize: '14px' }}>登録待ちデータ</div>
          </div>
        </div>
      </div>

      <div style={{ overflowX: 'auto' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
          <thead>
            <tr style={{ backgroundColor: '#f8f9fa' }}>
              <th style={{ padding: '12px', textAlign: 'left', fontWeight: 'bold', border: '1px solid #dee2e6' }}>ID</th>
              <th style={{ padding: '12px', textAlign: 'left', fontWeight: 'bold', border: '1px solid #dee2e6' }}>来訪者名</th>
              <th style={{ padding: '12px', textAlign: 'left', fontWeight: 'bold', border: '1px solid #dee2e6' }}>会社名</th>
              <th style={{ padding: '12px', textAlign: 'left', fontWeight: 'bold', border: '1px solid #dee2e6' }}>予約ID</th>
              <th style={{ padding: '12px', textAlign: 'left', fontWeight: 'bold', border: '1px solid #dee2e6' }}>会議室</th>
              <th style={{ padding: '12px', textAlign: 'left', fontWeight: 'bold', border: '1px solid #dee2e6' }}>登録日時</th>
              <th style={{ padding: '12px', textAlign: 'left', fontWeight: 'bold', border: '1px solid #dee2e6' }}>有効期限</th>
              <th style={{ padding: '12px', textAlign: 'left', fontWeight: 'bold', border: '1px solid #dee2e6' }}>データサイズ</th>
              <th style={{ padding: '12px', textAlign: 'left', fontWeight: 'bold', border: '1px solid #dee2e6' }}>使用回数</th>
              <th style={{ padding: '12px', textAlign: 'left', fontWeight: 'bold', border: '1px solid #dee2e6' }}>ステータス</th>
              <th style={{ padding: '12px', textAlign: 'left', fontWeight: 'bold', border: '1px solid #dee2e6' }}>操作</th>
            </tr>
          </thead>
          <tbody>
            {demoData.faceAuthData.map((item, index) => (
              <tr key={item.id} style={{ 
                backgroundColor: index % 2 === 0 ? '#ffffff' : '#f8f9fa',
                borderBottom: '1px solid #dee2e6' 
              }}>
                <td style={{ padding: '12px', border: '1px solid #dee2e6' }}>{item.id}</td>
                <td style={{ padding: '12px', border: '1px solid #dee2e6', fontWeight: '500' }}>{item.visitorName}</td>
                <td style={{ padding: '12px', border: '1px solid #dee2e6' }}>{item.company}</td>
                <td style={{ padding: '12px', border: '1px solid #dee2e6' }}>{item.reservationId}</td>
                <td style={{ padding: '12px', border: '1px solid #dee2e6' }}>{item.meetingRoom}</td>
                <td style={{ padding: '12px', border: '1px solid #dee2e6' }}>{item.registrationDate}</td>
                <td style={{ padding: '12px', border: '1px solid #dee2e6' }}>{item.expirationDate}</td>
                <td style={{ padding: '12px', border: '1px solid #dee2e6' }}>{item.faceDataSize}</td>
                <td style={{ padding: '12px', border: '1px solid #dee2e6' }}>{item.usageCount}</td>
                <td style={{ padding: '12px', border: '1px solid #dee2e6' }}>
                  <span style={{
                    padding: '4px 8px',
                    borderRadius: '4px',
                    fontSize: '12px',
                    fontWeight: '500',
                    backgroundColor: 
                      item.status === '有効' ? '#d4edda' : 
                      item.status === '未使用' ? '#fff3cd' : 
                      item.status === '期限切れ' ? '#f8d7da' : 
                      '#e2e3e5',
                    color: 
                      item.status === '有効' ? '#155724' : 
                      item.status === '未使用' ? '#856404' : 
                      item.status === '期限切れ' ? '#721c24' : 
                      '#383d41'
                  }}>
                    {item.status}
                  </span>
                </td>
                <td style={{ padding: '12px', border: '1px solid #dee2e6' }}>
                  <div style={{ display: 'flex', gap: '4px' }}>
                    <button 
                      onClick={() => alert(`${item.visitorName}の詳細情報を表示`)}
                      style={{
                        padding: '4px 8px',
                        backgroundColor: '#007bff',
                        color: 'white',
                        border: 'none',
                        borderRadius: '4px',
                        cursor: 'pointer',
                        fontSize: '12px'
                      }}
                    >
                      詳細
                    </button>
                    {item.status === '期限切れ' && (
                      <button 
                        onClick={() => alert(`${item.visitorName}の期限切れデータを削除`)}
                        style={{
                          padding: '4px 8px',
                          backgroundColor: '#dc3545',
                          color: 'white',
                          border: 'none',
                          borderRadius: '4px',
                          cursor: 'pointer',
                          fontSize: '12px'
                        }}
                      >
                        削除
                      </button>
                    )}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      
      <div style={{ marginTop: '20px', padding: '16px', backgroundColor: '#f8f9fa', borderRadius: '8px', border: '1px solid #dee2e6' }}>
        <h4 style={{ margin: '0 0 12px 0', display: 'flex', alignItems: 'center', gap: '8px' }}>
          ℹ️ 顔認証データについて
        </h4>
        <ul style={{ margin: 0, paddingLeft: '20px', color: '#6c757d' }}>
          <li>顔認証データは会議終了後24時間で自動削除されます</li>
          <li>未使用のデータは期限切れ後も手動削除まで保持されます</li>
          <li>データサイズは暗号化後のサイズを表示しています</li>
          <li>使用回数は顔認証システムでの認証成功回数です</li>
        </ul>
      </div>
    </div>
  )

  // エラーログタブ
  const renderErrorsTab = () => (
    <div style={{ background: 'white', borderRadius: '12px', padding: '24px', boxShadow: '0 2px 8px rgba(0,0,0,0.1)' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
        <h3 style={{ margin: 0, display: 'flex', alignItems: 'center', gap: '8px' }}>
          🚨 エラーログ発生・表示条件
        </h3>
        <div style={{ display: 'flex', gap: '10px' }}>
          <span style={{ fontSize: '12px' }}>詳細表示</span>
        </div>
      </div>
      
      <div style={{ marginBottom: '20px' }}>
        <h4 style={{ margin: '0 0 12px 0', display: 'flex', alignItems: 'center', gap: '8px' }}>
          🚨 エラーログ
        </h4>
        <div style={{ display: 'flex', gap: '10px', marginBottom: '16px' }}>
          <div>
            <span style={{ fontSize: '12px' }}>フィルタ:</span>
            <select style={{ padding: '4px', border: '1px solid #ccc', borderRadius: '4px', fontSize: '12px', marginLeft: '5px' }}>
              <option>すべて</option>
              <option>エラー</option>
              <option>警告</option>
              <option>情報</option>
            </select>
          </div>
          <div style={{ display: 'flex', gap: '10px', marginLeft: 'auto' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
              <span style={{ width: '12px', height: '12px', background: '#dc3545', borderRadius: '50%' }}></span>
              <span style={{ fontSize: '12px' }}>エラー: 8</span>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
              <span style={{ width: '12px', height: '12px', background: '#ffc107', borderRadius: '50%' }}></span>
              <span style={{ fontSize: '12px' }}>警告: 6</span>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
              <span style={{ width: '12px', height: '12px', background: '#17a2b8', borderRadius: '50%' }}></span>
              <span style={{ fontSize: '12px' }}>情報: 6</span>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
              <span style={{ width: '12px', height: '12px', background: '#28a745', borderRadius: '50%' }}></span>
              <span style={{ fontSize: '12px' }}>成功: 20</span>
            </div>
          </div>
        </div>
      </div>
      
      <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
        {demoData.errorLogs.map((log, index) => (
          <div key={log.id} style={{
            border: `1px solid ${log.category === 'error' ? '#dc3545' : log.category === 'warning' ? '#ffc107' : '#17a2b8'}`,
            borderLeft: `4px solid ${log.category === 'error' ? '#dc3545' : log.category === 'warning' ? '#ffc107' : '#17a2b8'}`,
            borderRadius: '4px',
            padding: '16px',
            background: log.category === 'error' ? '#fff5f5' : log.category === 'warning' ? '#fffbf0' : '#f0f9ff'
          }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
              <div style={{ flex: 1 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '8px' }}>
                  <span style={{ 
                    background: log.category === 'error' ? '#dc3545' : log.category === 'warning' ? '#ffc107' : '#17a2b8',
                    color: 'white',
                    padding: '2px 6px',
                    borderRadius: '4px',
                    fontSize: '10px',
                    fontWeight: 'bold'
                  }}>
                    {log.level}
                  </span>
                  <span style={{ fontSize: '12px', color: '#666' }}>
                    {log.timestamp} [{log.source}]
                  </span>
                </div>
                <div style={{ marginBottom: '8px', fontWeight: 'bold' }}>
                  {log.message}
                </div>
                <div style={{ fontSize: '12px', color: '#666', fontFamily: 'monospace' }}>
                  詳細: {log.details}
                </div>
              </div>
              <div>
                <button style={{ padding: '4px 8px', background: '#6c757d', color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer', fontSize: '12px' }}>
                  詳細
                </button>
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  )

  // メール監視タブ
  const renderMailTab = () => (
    <div style={{ background: 'white', borderRadius: '12px', padding: '24px', boxShadow: '0 2px 8px rgba(0,0,0,0.1)' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
        <h3 style={{ margin: 0, display: 'flex', alignItems: 'center', gap: '8px' }}>
          📧 メール監視
        </h3>
        <div style={{ display: 'flex', gap: '10px' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '5px' }}>
            <span style={{ fontSize: '12px' }}>📋 種別:</span>
            <select style={{ padding: '4px', border: '1px solid #ccc', borderRadius: '4px', fontSize: '12px' }}>
              <option>すべて</option>
              <option>送信済み</option>
              <option>エラー</option>
            </select>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '5px' }}>
            <span style={{ fontSize: '12px' }}>📅 期間:</span>
            <select style={{ padding: '4px', border: '1px solid #ccc', borderRadius: '4px', fontSize: '12px' }}>
              <option>今日</option>
              <option>今週</option>
              <option>今月</option>
            </select>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '5px' }}>
            <span style={{ fontSize: '12px' }}>🔍 検索:</span>
            <input type="text" placeholder="件名、送信者、宛先で検索" style={{ padding: '4px', border: '1px solid #ccc', borderRadius: '4px', fontSize: '12px' }} />
          </div>
          <div style={{ display: 'flex', gap: '5px' }}>
            <button style={{ padding: '4px 8px', background: '#28a745', color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer', fontSize: '12px' }}>
              📧 ExchangeOnline
            </button>
            <button style={{ padding: '4px 8px', background: '#007bff', color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer', fontSize: '12px' }}>
              📋 更新
            </button>
          </div>
        </div>
      </div>
      <div style={{ marginBottom: '20px', textAlign: 'right' }}>
        <span style={{ fontSize: '12px', color: '#666' }}>総メール数: 5　既読: 1050</span>
      </div>
      
      <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
        {demoData.mailMonitoring.map((mail, index) => (
          <div key={mail.id} style={{
            border: '1px solid #e9ecef',
            borderRadius: '8px',
            padding: '16px',
            background: '#f8f9fa'
          }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '8px' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <span style={{ 
                  background: '#28a745',
                  color: 'white',
                  padding: '2px 6px',
                  borderRadius: '4px',
                  fontSize: '10px'
                }}>
                  ✓ {mail.status}
                </span>
                <span style={{ fontSize: '12px', color: '#666' }}>{mail.timestamp}</span>
              </div>
              <div style={{ display: 'flex', gap: '4px' }}>
                {mail.tags.map((tag, tagIndex) => (
                  <span key={tagIndex} style={{ 
                    background: '#17a2b8',
                    color: 'white',
                    padding: '2px 6px',
                    borderRadius: '4px',
                    fontSize: '10px'
                  }}>
                    {tag}
                  </span>
                ))}
              </div>
            </div>
            <div style={{ fontWeight: 'bold', marginBottom: '8px' }}>
              {mail.subject}
            </div>
            <div style={{ fontSize: '12px', color: '#666', marginBottom: '8px' }}>
              <div>差出人: {mail.from}</div>
              <div>宛先: {mail.to}</div>
            </div>
            <div style={{ fontSize: '12px', color: '#333', marginBottom: '8px' }}>
              {mail.content}
            </div>
            <div style={{ display: 'flex', gap: '8px' }}>
              <button style={{ padding: '4px 8px', background: '#007bff', color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer', fontSize: '12px' }}>
                📋 詳細表示
              </button>
            </div>
          </div>
        ))}
      </div>
    </div>
  )

  // 予測分析タブ（別版）
  const renderPredictiveTab = () => (
    <div style={{ background: 'white', borderRadius: '12px', padding: '24px', boxShadow: '0 2px 8px rgba(0,0,0,0.1)' }}>
      <div style={{ 
        background: 'linear-gradient(135deg, #6B73FF 0%, #9B59B6 100%)',
        color: 'white',
        padding: '20px',
        borderRadius: '12px',
        marginBottom: '20px'
      }}>
        <h2 style={{ margin: 0, display: 'flex', alignItems: 'center', gap: '10px' }}>
          🔮 予測分析ダッシュボード
        </h2>
        <p style={{ margin: '5px 0 0 0', opacity: 0.9 }}>
          AIによる来訪者・会議室・セキュリティの予測分析
        </p>
      </div>
      
      <div style={{ display: 'flex', gap: '20px', flexWrap: 'wrap' }}>
        <div style={{ flex: '1 1 300px', minHeight: '200px', background: '#f8f9fa', borderRadius: '8px', padding: '16px' }}>
          <h4>📈 来訪者トレンド予測</h4>
          <div style={{ textAlign: 'center', marginTop: '40px' }}>
            <div style={{ fontSize: '24px', marginBottom: '8px' }}>📊</div>
            <div>実績と予測の比較</div>
          </div>
        </div>
        
        <div style={{ flex: '1 1 300px', minHeight: '200px', background: '#f8f9fa', borderRadius: '8px', padding: '16px' }}>
          <h4>🏢 会議室利用率予測</h4>
          <div style={{ textAlign: 'center', marginTop: '40px' }}>
            <div style={{ fontSize: '24px', marginBottom: '8px' }}>🏢</div>
            <div>現在と予測の利用率比較</div>
          </div>
        </div>
        
        <div style={{ flex: '1 1 300px', minHeight: '200px', background: '#f8f9fa', borderRadius: '8px', padding: '16px' }}>
          <h4>⏰ ピーク時間分析</h4>
          <div style={{ textAlign: 'center', marginTop: '40px' }}>
            <div style={{ fontSize: '24px', marginBottom: '8px' }}>⏰</div>
            <div>時間帯別来訪者数予測</div>
          </div>
        </div>
      </div>
    </div>
  )

  // 設定タブ
  const renderSettingsTab = () => (
    <div style={{ display: 'flex', gap: '20px', flexWrap: 'wrap' }}>
      {/* API設定状態 */}
      <div style={{ flex: '1 1 45%', minWidth: '400px', background: 'white', borderRadius: '12px', padding: '24px', boxShadow: '0 2px 8px rgba(0,0,0,0.1)' }}>
        <h4 style={{ margin: '0 0 16px 0', display: 'flex', alignItems: 'center', gap: '8px' }}>
          🔧 API設定状態
        </h4>
        <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
          {Object.entries(demoData.settings.apiStatus).map(([api, status]) => (
            <div key={api} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '12px', background: '#f8f9fa', borderRadius: '6px' }}>
              <span style={{ fontWeight: 'bold' }}>{api}:</span>
              <span style={{ 
                color: status === '設定済み' ? '#28a745' : '#dc3545',
                fontWeight: 'bold'
              }}>
                {status === '設定済み' ? '✓ 設定済み' : '✗ 未設定'}
              </span>
            </div>
          ))}
        </div>
      </div>

      {/* システムメンテナンス */}
      <div style={{ flex: '1 1 45%', minWidth: '400px', background: 'white', borderRadius: '12px', padding: '24px', boxShadow: '0 2px 8px rgba(0,0,0,0.1)' }}>
        <h4 style={{ margin: '0 0 16px 0', display: 'flex', alignItems: 'center', gap: '8px' }}>
          🔧 システムメンテナンス
        </h4>
        <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
          <button 
            onClick={() => performMaintenance('backup')}
            style={{ 
              padding: '12px 16px', 
              background: '#007bff', 
              color: 'white', 
              border: 'none', 
              borderRadius: '6px', 
              cursor: 'pointer',
              fontWeight: '600'
            }}
          >
            💾 バックアップ作成
          </button>
          <button 
            onClick={() => performMaintenance('cleanup')}
            style={{ 
              padding: '12px 16px', 
              background: '#28a745', 
              color: 'white', 
              border: 'none', 
              borderRadius: '6px', 
              cursor: 'pointer',
              fontWeight: '600'
            }}
          >
            🗑️ 古いデータクリーンアップ
          </button>
          <button 
            onClick={() => performMaintenance('sync')}
            style={{ 
              padding: '12px 16px', 
              background: '#17a2b8', 
              color: 'white', 
              border: 'none', 
              borderRadius: '6px', 
              cursor: 'pointer',
              fontWeight: '600'
            }}
          >
            🔄 カレンダー同期
          </button>
          <button 
            onClick={() => performMaintenance('usage')}
            style={{ 
              padding: '12px 16px', 
              background: '#ffc107', 
              color: 'black', 
              border: 'none', 
              borderRadius: '6px', 
              cursor: 'pointer',
              fontWeight: '600'
            }}
          >
            📊 API使用量チェック
          </button>
        </div>
      </div>

      {/* スケジューラー状態 */}
      <div style={{ flex: '1 1 45%', minWidth: '400px', background: 'white', borderRadius: '12px', padding: '24px', boxShadow: '0 2px 8px rgba(0,0,0,0.1)' }}>
        <h4 style={{ margin: '0 0 16px 0', display: 'flex', alignItems: 'center', gap: '8px' }}>
          ⏰ 自動タスク状態
        </h4>
        <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '12px', background: '#f8f9fa', borderRadius: '6px' }}>
            <span style={{ fontWeight: 'bold' }}>顔認証データ削除:</span>
            <span style={{ color: '#28a745', fontWeight: 'bold' }}>🟢 毎時実行</span>
          </div>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '12px', background: '#f8f9fa', borderRadius: '6px' }}>
            <span style={{ fontWeight: 'bold' }}>バックアップ作成:</span>
            <span style={{ color: '#28a745', fontWeight: 'bold' }}>🟢 毎日2:00</span>
          </div>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '12px', background: '#f8f9fa', borderRadius: '6px' }}>
            <span style={{ fontWeight: 'bold' }}>API使用量チェック:</span>
            <span style={{ color: '#28a745', fontWeight: 'bold' }}>🟢 毎日8:00</span>
          </div>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '12px', background: '#f8f9fa', borderRadius: '6px' }}>
            <span style={{ fontWeight: 'bold' }}>システムヘルスチェック:</span>
            <span style={{ color: '#28a745', fontWeight: 'bold' }}>🟢 15分毎</span>
          </div>
        </div>
      </div>

      {/* 運用に関する注意事項 */}
      <div style={{ flex: '1 1 45%', minWidth: '400px', background: 'white', borderRadius: '12px', padding: '24px', boxShadow: '0 2px 8px rgba(0,0,0,0.1)' }}>
        <h4 style={{ margin: '0 0 16px 0', display: 'flex', alignItems: 'center', gap: '8px' }}>
          ⚠️ 運用に関する注意事項
        </h4>
        <div style={{ background: '#fff3cd', border: '1px solid #ffeaa7', borderRadius: '6px', padding: '16px' }}>
          <ul style={{ margin: 0, paddingLeft: '20px' }}>
            {demoData.settings.notes.map((note, index) => (
              <li key={index} style={{ marginBottom: '8px', lineHeight: '1.5' }}>
                {note}
              </li>
            ))}
          </ul>
        </div>
      </div>
    </div>
  )

  // メンテナンス機能の実行
  const performMaintenance = async (action) => {
    try {
      let message = ''
      
      switch(action) {
        case 'backup':
          message = '💾 バックアップを作成しています...'
          console.log('Creating backup...')
          const backupResponse = await fetch('/api/admin/backup/create', { method: 'POST' })
          const backupResult = await backupResponse.json()
          if (backupResult.success) {
            message = `✅ バックアップが完了しました: ${backupResult.backup.filename}`
          } else {
            throw new Error(backupResult.error)
          }
          break
        case 'cleanup':
          message = '🗑️ 古いデータをクリーンアップしています...'
          console.log('Cleaning up old data...')
          const cleanupResponse = await fetch('/api/admin/cleanup/face-auth', { method: 'POST' })
          const cleanupResult = await cleanupResponse.json()
          if (cleanupResult.success) {
            message = `✅ クリーンアップが完了しました: ${cleanupResult.message}`
          } else {
            throw new Error(cleanupResult.error)
          }
          break
        case 'sync':
          message = '🔄 カレンダー同期を実行しています...'
          console.log('Syncing calendar...')
          const syncResponse = await fetch('/api/admin/calendar/sync', { 
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ userEmail: 'admin@example.com' })
          })
          const syncResult = await syncResponse.json()
          if (syncResult.success) {
            message = `✅ カレンダー同期が完了しました: ${syncResult.sync.syncedCount || 0}件の予定を処理`
          } else {
            throw new Error(syncResult.error)
          }
          break
        case 'usage':
          message = '📊 API使用量をチェックしています...'
          console.log('Checking API usage...')
          const usageResponse = await fetch('/api/admin/azure/usage')
          const usageResult = await usageResponse.json()
          if (usageResult.success) {
            const faceUsage = usageResult.usage.faceApi
            const speechUsage = usageResult.usage.speechApi
            message = `📊 API使用量:\n Face API: ${faceUsage.estimatedUsage}/${faceUsage.monthlyLimit}\n Speech API: ${speechUsage.estimatedUsage}/${speechUsage.monthlyLimit}`
          } else {
            throw new Error(usageResult.error)
          }
          break
      }
      
      alert(message)
      
    } catch (error) {
      console.error('Maintenance error:', error)
      alert(`メンテナンス操作でエラーが発生しました: ${error.message}`)
    }
  }

  return (
    <div style={{
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      background: 'linear-gradient(135deg, #f8f9fa 0%, #ffffff 100%)',
      display: 'flex',
      flexDirection: 'column',
      overflow: 'hidden',
      zIndex: 1000
    }}>
      {/* ヘッダー */}
      <div style={{
        background: 'linear-gradient(135deg, #667eea 0%, #764ba2 50%, #f093fb 100%)',
        color: 'white',
        padding: '25px 30px',
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        position: 'relative',
        boxShadow: '0 8px 32px rgba(0, 0, 0, 0.2)'
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
          <div style={{ 
            fontSize: '32px',
            filter: 'drop-shadow(0 4px 8px rgba(0, 0, 0, 0.3))',
            animation: 'pulse 2s infinite'
          }}>🛡️</div>
          <div>
            <h1 style={{ 
              margin: 0, 
              fontSize: '28px', 
              fontWeight: '700',
              textShadow: '0 2px 4px rgba(0, 0, 0, 0.3)',
              letterSpacing: '0.5px'
            }}>🔧 管理ダッシュボード</h1>
            <p style={{ 
              margin: '4px 0 0 0', 
              fontSize: '14px', 
              opacity: 0.9,
              fontWeight: '300'
            }}>ALSOK AI受付システム総合管理</p>
          </div>
        </div>
        <button
          onClick={onClose}
          style={{
            background: 'rgba(255, 255, 255, 0.2)',
            color: 'white',
            border: 'none',
            borderRadius: '50%',
            width: '50px',
            height: '50px',
            cursor: 'pointer',
            fontSize: '24px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            transition: 'all 0.3s ease',
            backdropFilter: 'blur(10px)'
          }}
          onMouseOver={(e) => {
            e.target.style.background = 'rgba(255, 255, 255, 0.3)'
            e.target.style.transform = 'scale(1.1)'
          }}
          onMouseOut={(e) => {
            e.target.style.background = 'rgba(255, 255, 255, 0.2)'
            e.target.style.transform = 'scale(1)'
          }}
        >
          ✕
        </button>
      </div>

      {/* タブナビゲーション */}
      <div style={{
        background: 'linear-gradient(90deg, #f8f9fa 0%, #ffffff 100%)',
        borderBottom: '2px solid #e9ecef',
        padding: '0 30px',
        display: 'flex',
        gap: '4px',
        overflowX: 'auto',
        boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)'
      }}>
        {tabs.map((tab) => (
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id)}
            style={{
              background: activeTab === tab.id 
                ? 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)' 
                : 'transparent',
              color: activeTab === tab.id ? 'white' : '#495057',
              border: activeTab === tab.id ? 'none' : '1px solid transparent',
              borderBottom: 'none',
              padding: '16px 20px',
              cursor: 'pointer',
              fontSize: '15px',
              fontWeight: activeTab === tab.id ? '600' : '500',
              whiteSpace: 'nowrap',
              marginBottom: activeTab === tab.id ? '-2px' : '0',
              borderRadius: activeTab === tab.id ? '12px 12px 0 0' : '8px 8px 0 0',
              transition: 'all 0.3s ease',
              boxShadow: activeTab === tab.id 
                ? '0 -4px 20px rgba(102, 126, 234, 0.4)' 
                : '0 2px 8px rgba(0, 0, 0, 0.05)',
              transform: activeTab === tab.id ? 'translateY(-2px)' : 'translateY(0)',
              textShadow: activeTab === tab.id ? '0 1px 2px rgba(0, 0, 0, 0.2)' : 'none'
            }}
            onMouseOver={(e) => {
              if (activeTab !== tab.id) {
                e.target.style.background = 'linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%)'
                e.target.style.transform = 'translateY(-1px)'
              }
            }}
            onMouseOut={(e) => {
              if (activeTab !== tab.id) {
                e.target.style.background = 'transparent'
                e.target.style.transform = 'translateY(0)'
              }
            }}
          >
            <span style={{ marginRight: '8px' }}>{tab.icon}</span>
            {tab.label}
          </button>
        ))}
      </div>

      {/* コンテンツエリア */}
      <div style={{
        flex: 1,
        padding: '30px',
        overflow: 'auto',
        background: 'linear-gradient(135deg, #f8f9fa 0%, #ffffff 100%)',
        position: 'relative'
      }}>
        {renderTabContent()}
      </div>
      <style>
        {`
          @keyframes pulse {
            0%, 100% { transform: scale(1); }
            50% { transform: scale(1.05); }
          }
        `}
      </style>
    </div>
  )
}

export default AdminDashboard